import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  WhatsappLogo, Spinner, X, Phone, ArrowRight, Check,
  Calendar, ChatCircle, Sparkle, Gauge, ChartLineUp,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { BULK_GREETINGS_LABEL_LONG } from "@/lib/labels";

// iter v29 r226 — Persistenza locale dello stato "inviati" così l'agente può
// chiudere e riaprire Pronto senza perdere il progresso (es. interruzione
// per chiamata, batteria, multitasking iOS). Key per (occasione + numero
// contatti) così se cambia il batch la sessione si resetta.
const LS_PROGRESS_PREFIX = "pronto:bulk-greet-progress:";
const PROGRESS_TTL_MS = 1000 * 60 * 60 * 24; // 24h cleanup

function buildSessionKey(occasion, items) {
  if (!items?.length) return null;
  return `${LS_PROGRESS_PREFIX}${occasion}:${items.length}:${items[0]?.contact_id || ""}`;
}

function loadProgress(sessionKey) {
  if (!sessionKey) return null;
  try {
    const raw = localStorage.getItem(sessionKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.startedAt || Date.now() - data.startedAt > PROGRESS_TTL_MS) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function saveProgress(sessionKey, sentIds, startedAt) {
  if (!sessionKey) return;
  try {
    localStorage.setItem(sessionKey, JSON.stringify({ sentIds, startedAt }));
  } catch { /* quota */ }
}

function clearProgress(sessionKey) {
  if (!sessionKey) return;
  try { localStorage.removeItem(sessionKey); } catch { /* ignore */ }
}

/**
 * BulkGreetingsModal — auguri di gruppo formali (Lei) per festività e
 * occasioni personali. Genera messaggi WhatsApp personalizzati con AI per
 * tutti i contatti selezionati e li apre uno alla volta in WhatsApp.
 *
 * Niente invio automatico: l'agente clicca "Apri WhatsApp" → verifica →
 * manda → "Avanti" → contatto successivo. Sequenza guidata.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 */
export default function BulkGreetingsModal({ open, onClose, prefillOccasion, prefillCustomText, prefillContactIds, prefillMatchInfo, prefillPropertyId, prefillPersonalize }) {
  const [phase, setPhase] = useState("pick"); // pick | loading | sending
  const [occasions, setOccasions] = useState([]);
  const [occasion, setOccasion] = useState("");
  const [customText, setCustomText] = useState("");
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [sentIds, setSentIds] = useState({});
  const [contactsCheck, setContactsCheck] = useState({ loading: true, count: 0, withPhone: 0 });

  // iter v28 r400 — User M1310: lista contatti selezionabili + ricerca +
  // filtri (sesso, stato). Era macchinoso dover navigare 1 contatto alla
  // volta senza poter scegliere CHI ricevere il messaggio.
  const [allContacts, setAllContacts] = useState([]);
  const [selectedSet, setSelectedSet] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [filterGender, setFilterGender] = useState(""); // "" | "m" | "f"
  const [filterStatus, setFilterStatus] = useState(""); // "" | "caldo" | "tiepido" | "freddo"
  // iter v29 r044 — Schedula per dopo
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  // iter v29 r226 — NUOVE FEATURES:
  // 1. personalize: opt-in toggle che usa LLM per arricchire ogni messaggio
  //    con il contesto del singolo cliente (note + ultima interazione).
  //    Costa ~1 credito ogni 10 contatti (decide il backend).
  // iter v32 r49d — Quando arriviamo dal flow "Notifica clienti interessati"
  // (prefillPersonalize=true) il toggle parte già attivo: c'è un immobile
  // specifico da proporre + match reasons → la personalizzazione ha valore.
  const [personalize, setPersonalize] = useState(false);
  // 2. startedAt: timestamp avvio sessione per calcolare durata totale
  const [startedAt, setStartedAt] = useState(null);
  // 3. speed: velocità auto-advance (s) — VELOCE 2s · NORMALE 4s · CALMO 8s
  const [speed, setSpeed] = useState(4);
  // 4. sessionKey derivata: chiave localStorage per persistenza progressi
  const sessionKey = useMemo(() => buildSessionKey(occasion, items), [occasion, items]);

  // Persisti sentIds in localStorage ad ogni cambio (solo durante "sending")
  useEffect(() => {
    if (!sessionKey || phase !== "sending") return;
    saveProgress(sessionKey, sentIds, startedAt || Date.now());
  }, [sentIds, sessionKey, phase, startedAt]);

  useEffect(() => {
    if (!open) return;
    setPhase("pick");
    // iter v28 r386 — Quando arriviamo dal popup compleanno (birthday-prompt),
    // prefillOccasion="compleanno_app" è una OCCASIONE REALE (non "custom"),
    // quindi non dobbiamo forzare "custom" come per prefillCustomText
    // libero (festività con messaggio già pronto). Distinguiamo i due casi.
    if (prefillOccasion && prefillOccasion !== "custom") {
      setOccasion(prefillOccasion);
    } else {
      setOccasion(prefillOccasion ? "custom" : "");
    }
    setCustomText(prefillCustomText || "");
    setItems([]);
    setActiveIdx(0);
    setSentIds({});
    setAllContacts([]);
    setSelectedSet(new Set());
    setSearchQuery("");
    setFilterGender("");
    setFilterStatus("");
    setContactsCheck({ loading: true, count: 0, withPhone: 0 });
    // iter v32 r49d — Auto-toggle "personalizza" se arriviamo da match con
    // immobile allegato. L'agente può sempre disattivarlo.
    setPersonalize(!!prefillPersonalize);
    (async () => {
      try {
        const [occR, ctR] = await Promise.all([
          api.get("/contacts/bulk-greetings/occasions"),
          api.get("/contacts"),
        ]);
        setOccasions(occR.data?.occasions || []);
        const contacts = ctR.data?.items || ctR.data || [];
        const withPhoneContacts = contacts.filter((c) => (c.phone || c.tel || "").trim());
        setContactsCheck({ loading: false, count: contacts.length, withPhone: withPhoneContacts.length });
        setAllContacts(withPhoneContacts);
        // iter v28 r400 — se l'utente arriva con prefillContactIds (dal
        // modal "Notifica clienti interessati" o dalla bulk-bar in
        // /contatti), seleziono già quei contatti.
        if (Array.isArray(prefillContactIds) && prefillContactIds.length > 0) {
          setSelectedSet(new Set(prefillContactIds));
        }
      } catch {
        toast.error("Impossibile caricare i contatti");
        setContactsCheck({ loading: false, count: 0, withPhone: 0 });
      }
    })();
  }, [open, prefillOccasion, prefillCustomText, prefillContactIds, prefillPersonalize, prefillPropertyId]);

  // iter v28 r400 — Dopo "Continua" dalla fase pick, andiamo a "select"
  // dove l'utente vede TUTTI i contatti con phone e può:
  //   • cercare per nome/telefono
  //   • filtrare per sesso (Uomini / Donne / tutti)
  //   • filtrare per stato (Da chiamare / Da risentire / In pausa)
  //   • selezionare individualmente o "tutti i visibili"
  // Solo dopo, premendo "Avanti", generiamo i messaggi per i selezionati.
  const goToSelect = () => {
    if (!occasion) {
      toast.error("Scegli un'occasione prima di continuare");
      return;
    }
    if (occasion === "custom" && !customText.trim()) {
      toast.error("Scrivi il messaggio personalizzato");
      return;
    }
    if (allContacts.length === 0) {
      toast.error("Nessun contatto con numero di telefono");
      return;
    }
    setPhase("select");
  };

  const generate = async () => {
    if (selectedSet.size === 0) {
      toast.error("Seleziona almeno un contatto");
      return;
    }
    setPhase("loading");
    try {
      const payload = {
        occasion,
        custom_text: customText || null,
        contact_ids: Array.from(selectedSet),
        // iter v29 r226 — opt-in personalizzazione LLM per singolo cliente
        personalize: !!personalize,
        // iter v32 r49d — Se arriviamo da match (immobile selezionato),
        // mandiamo il property_id così l'LLM cita titolo/zona/prezzo nel
        // messaggio invece di personalizzare al buio.
        property_id: prefillPropertyId || null,
      };
      const { data } = await api.post("/contacts/bulk-greetings", payload);
      const list = (data?.items || []).filter((i) => i.wa_url);
      if (list.length === 0) {
        toast.error("Nessun messaggio generato. Verifica che i contatti abbiano il numero.");
        setPhase("select");
        return;
      }
      setItems(list);
      setActiveIdx(0);
      // iter v29 r226 — Recupera progresso precedente se esiste
      const key = buildSessionKey(occasion, list);
      const prev = loadProgress(key);
      if (prev?.sentIds && Object.keys(prev.sentIds).length > 0) {
        setSentIds(prev.sentIds);
        setStartedAt(prev.startedAt);
        // Vai all'ultimo contatto non ancora inviato
        const firstUnsent = list.findIndex((it) => !prev.sentIds[it.contact_id]);
        setActiveIdx(firstUnsent >= 0 ? firstUnsent : 0);
        toast.success(`Ripresa sessione · ${Object.keys(prev.sentIds).length}/${list.length} già inviati`);
      } else {
        setSentIds({});
        setStartedAt(Date.now());
      }
      setPhase("sending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generazione fallita");
      setPhase("select");
    }
  };

  // iter v28 r399 — User M1309: lo screenshot mostrava il toast rosso
  // "Nessun contatto con numero" rimasto visibile anche dopo aver
  // aggiunto un numero a un contatto. È un toast stale di un tentativo
  // precedente. Dismiss esplicito quando l'utente apre o cambia phase
  // così non resta inquinato lo schermo.
  useEffect(() => {
    if (open) {
      toast.dismiss();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
      data-testid="bulk-greetings-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 sm:px-6 py-4 border-b border-[#EAE7DF] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#7C8B6E]/12 flex items-center justify-center">
            <WhatsappLogo size={18} weight="duotone" className="text-[#7C8B6E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-[#1C1C1A] tracking-tight">
              {BULK_GREETINGS_LABEL_LONG}
            </div>
            <div className="text-[11.5px] text-[#6B6A65]">
              {phase === "pick" && "1 di 3 · Scegli un'occasione"}
              {phase === "select" && `2 di 3 · ${selectedSet.size} su ${allContacts.length} selezionati`}
              {phase === "loading" && "Apertura messaggi…"}
              {phase === "sending" && `3 di 3 · ${activeIdx + 1}/${items.length} · apri WhatsApp e manda`}
              {phase === "summary" && "Sessione conclusa · riepilogo"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-[#F5F4F0] flex items-center justify-center text-[#6B6A65]"
            data-testid="bulk-greetings-close"
            aria-label="Chiudi"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          {phase === "pick" && contactsCheck.loading && (
            <div className="py-10 flex items-center justify-center">
              <Spinner size={20} className="animate-spin text-[#7C8B6E]" />
            </div>
          )}
          {phase === "pick" && !contactsCheck.loading && contactsCheck.withPhone === 0 && (
            <div className="py-2 space-y-4" data-testid="bulk-greetings-empty">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-[14px] font-semibold text-amber-900 mb-1">
                  Non hai ancora contatti con numero di telefono
                </div>
                <div className="text-[12.5px] text-amber-800 leading-relaxed">
                  {contactsCheck.count === 0 ? (
                    <>Per mandare messaggi WhatsApp di gruppo serve <strong>almeno un contatto col numero</strong>. Aggiungili dalla pagina <strong>Contatti</strong>, oppure carica un file <strong>Excel, CSV o vCard</strong>.</>
                  ) : (
                    <>Hai <strong>{contactsCheck.count} contatti</strong> in rubrica, ma nessuno ha un numero di telefono. Apri i contatti e aggiungi i numeri per usare i messaggi di gruppo.</>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href="/dashboard/contatti"
                  className="flex-1 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-semibold py-2.5 rounded-md text-center"
                  data-testid="bulk-greetings-goto-contacts"
                >
                  Vai ai contatti
                </a>
                <a
                  href="/dashboard/importa-gestionale"
                  className="flex-1 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#1C1C1A] text-[13px] font-medium py-2.5 rounded-md text-center"
                  data-testid="bulk-greetings-import-file"
                >
                  Importa da Excel / CSV
                </a>
              </div>
            </div>
          )}
          {phase === "pick" && !contactsCheck.loading && contactsCheck.withPhone > 0 && (
            <>
              <div className="mb-4 p-2.5 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded text-[11.5px] text-[#3F3D38] leading-snug">
                <strong className="text-[#7C8B6E]">{contactsCheck.withPhone} contatti</strong> in rubrica con numero di telefono. Scegli un'occasione, Pronto prepara il messaggio pronto. Tu apri i contatti uno alla volta in WhatsApp e mandi.
              </div>
              <PickPhase
                occasions={occasions}
                occasion={occasion}
                setOccasion={setOccasion}
                customText={customText}
                setCustomText={setCustomText}
              />
              {/* iter v29 r226 — Toggle personalizzazione LLM per singolo cliente */}
              {occasion && occasion !== "custom" && occasion !== "broadcast" && (
                <PersonalizeToggle personalize={personalize} setPersonalize={setPersonalize} />
              )}
            </>
          )}
          {/* iter v28 r400 — Fase 2: lista contatti con ricerca + filtri */}
          {phase === "select" && (
            <SelectContactsPhase
              allContacts={allContacts}
              selectedSet={selectedSet}
              setSelectedSet={setSelectedSet}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterGender={filterGender}
              setFilterGender={setFilterGender}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              matchInfo={prefillMatchInfo}
            />
          )}
          {phase === "loading" && (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <Spinner size={36} className="text-[#7C8B6E] animate-spin mb-3" />
              <div className="text-[13.5px] text-[#1C1C1A] font-medium">
                Apertura messaggi…
              </div>
            </div>
          )}
          {phase === "sending" && items[activeIdx] && (
            <SendPhase
              item={items[activeIdx]}
              sentIds={sentIds}
              setSentIds={setSentIds}
              total={items.length}
              activeIdx={activeIdx}
              speed={speed}
              setSpeed={setSpeed}
              personalized={!!personalize}
              onPrev={() => setActiveIdx((i) => Math.max(0, i - 1))}
              onNext={() => setActiveIdx((i) => Math.min(items.length - 1, i + 1))}
              onClose={onClose}
              onFinish={() => setPhase("summary")}
            />
          )}
          {phase === "summary" && (
            <SummaryPhase
              items={items}
              sentIds={sentIds}
              occasion={occasion}
              startedAt={startedAt}
              onRestart={() => {
                clearProgress(sessionKey);
                setSentIds({});
                setStartedAt(Date.now());
                setActiveIdx(0);
                setPhase("sending");
              }}
              onClose={() => {
                clearProgress(sessionKey);
                onClose();
              }}
            />
          )}
        </div>

        {/* Footer */}
        {phase === "pick" && (
          <div className="px-5 sm:px-6 py-3 border-t border-[#EAE7DF] flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3.5 py-2 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium"
              data-testid="bulk-greetings-cancel"
            >
              Annulla
            </button>
            <button
              onClick={goToSelect}
              disabled={!occasion || contactsCheck.withPhone === 0 || contactsCheck.loading}
              className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bulk-greetings-generate"
            >
              Avanti · scegli contatti <ArrowRight size={13} weight="bold" />
            </button>
          </div>
        )}
        {/* iter v28 r400 — Footer fase select */}
        {phase === "select" && (
          <div className="px-5 sm:px-6 py-3 border-t border-[#EAE7DF] space-y-2">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setPhase("pick")}
                className="px-3.5 py-2 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium"
                data-testid="bulk-greetings-back"
              >
                ← Indietro
              </button>
              <button
                onClick={generate}
                disabled={selectedSet.size === 0}
                className="inline-flex items-center gap-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-greetings-send"
              >
                Apri messaggi per {selectedSet.size} {selectedSet.size === 1 ? "contatto" : "contatti"}
                <ArrowRight size={13} weight="bold" />
              </button>
            </div>
            {/* iter v29 r044 — Toggle "Schedula per dopo" */}
            <button
              onClick={() => setShowScheduler(true)}
              disabled={selectedSet.size === 0}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-[#1C1C1A] border border-[#1C1C1A]/30 hover:border-[#1C1C1A] hover:bg-[#F8F7F2] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="bulk-greetings-schedule-toggle"
            >
              <Calendar size={13} weight="bold" />
              Schedula per dopo (es. domenica 09:00)
            </button>
          </div>
        )}
        {phase === "sending" && (
          <div className="px-5 sm:px-6 py-3 border-t border-[#EAE7DF] flex items-center justify-between">
            <div className="flex items-center gap-1">
              {items.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i === activeIdx ? "bg-[#7C8B6E]" : sentIds[items[i].contact_id] ? "bg-[#7C8B6E]/40" : "bg-[#E5E7E0]"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={onClose}
              className="text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium"
              data-testid="bulk-greetings-end"
            >
              Fine
            </button>
          </div>
        )}

        {/* iter v29 r044 — Sheet "Schedula per dopo" */}
        {showScheduler && (
          <ScheduleSheet
            occasion={occasion}
            customText={customText}
            selectedSet={selectedSet}
            allContacts={allContacts}
            scheduling={scheduling}
            setScheduling={setScheduling}
            onClose={() => setShowScheduler(false)}
            onScheduled={() => { setShowScheduler(false); onClose(); }}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * ScheduleSheet · sheet bottom modale per pianificare l'invio
 * Suggerisce alcuni orari standard (domenica mattina, fra 1 ora,
 * la sera precedente di Natale, etc.) e permette anche custom.
 * ───────────────────────────────────────────────────────────── */
function ScheduleSheet({ occasion, customText, selectedSet, allContacts, scheduling, setScheduling, onClose, onScheduled }) {
  // Pre-set: date+time defaults locali
  const tzOffsetMin = new Date().getTimezoneOffset(); // minuti tra UTC e locale (positivo = UTC indietro)
  const fmtLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const nextSundayMorning = () => {
    const d = new Date();
    const days = (7 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return d;
  };
  const tomorrow9 = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  };
  const in1Hour = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setMinutes(0, 0, 0);
    return d;
  };

  const [dt, setDt] = useState(fmtLocal(nextSundayMorning()));

  const presets = [
    { label: "Fra 1 ora", value: fmtLocal(in1Hour()) },
    { label: "Domani 09:00", value: fmtLocal(tomorrow9()) },
    { label: "Domenica 09:00", value: fmtLocal(nextSundayMorning()) },
  ];

  const handleSubmit = async () => {
    if (scheduling) return;
    setScheduling(true);
    try {
      const dtLocal = new Date(dt);
      if (Number.isNaN(dtLocal.getTime())) {
        toast.error("Data non valida");
        setScheduling(false);
        return;
      }
      const isoUtc = dtLocal.toISOString();
      const payload = {
        occasion,
        scheduled_at: isoUtc,
        contact_ids: Array.from(selectedSet),
        custom_text: customText || "",
        timezone_offset_min: tzOffsetMin,
        title: `Auguri ${occasion} · ${selectedSet.size} contatti`,
      };
      const r = await api.post("/scheduled-greetings", payload);
      if (r?.data?.ok) {
        const dStr = dtLocal.toLocaleString("it-IT", {
          weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
        });
        toast.success(`Pianificato per ${dStr}. Ti mandiamo un'email-promemoria quando è ora.`);
        onScheduled();
      } else {
        toast.error("Errore: " + (r?.data?.error || "riprova"));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div
      className="absolute inset-0 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 py-3"
      onClick={onClose}
      data-testid="bulk-greetings-schedule-sheet"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#EAE7DF] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={18} weight="duotone" className="text-[#1C1C1A]" />
            <div className="text-[14.5px] font-semibold text-[#1C1C1A]">Schedula per dopo</div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full hover:bg-[#F8F7F2] flex items-center justify-center text-[#6B6A65]"
            data-testid="bulk-greetings-schedule-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-[12.5px] text-[#6B6A65] leading-snug">
            Pronto invierà a te un'<strong>email-promemoria</strong> all'orario scelto.
            Apri Pronto, premi <strong>Avvia sequenza guidata</strong> e mandi su WhatsApp uno per uno.
          </div>

          {/* Preset rapidi */}
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => setDt(p.value)}
                className={`px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-colors ${
                  dt === p.value
                    ? "bg-[#1C1C1A] text-white border-[#1C1C1A]"
                    : "bg-white text-[#1C1C1A] border-[#EAE7DF] hover:border-[#1C1C1A]"
                }`}
                data-testid={`bulk-greetings-schedule-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date+time input custom */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5">
              Oppure scegli data e ora
            </label>
            <input
              type="datetime-local"
              value={dt}
              onChange={(e) => setDt(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[#EAE7DF] text-[14px] font-semibold text-[#1C1C1A] bg-white focus:outline-none focus:border-[#1C1C1A]"
              min={fmtLocal(new Date(Date.now() + 3 * 60 * 1000))}
              data-testid="bulk-greetings-schedule-datetime"
            />
          </div>

          <div className="text-[11.5px] text-[#6B6A65]">
            Saranno inclusi <strong>{selectedSet.size}</strong> {selectedSet.size === 1 ? "contatto" : "contatti"} selezionati ora.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#EAE7DF] flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            className="px-3.5 py-2 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={scheduling || selectedSet.size === 0}
            className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-greetings-schedule-confirm"
          >
            {scheduling ? <><Spinner size={13} className="animate-spin" /> Pianifico…</> : <>Pianifica → <Check size={13} weight="bold" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// iter v28 r400 — Lista contatti con ricerca + filtri (sesso, stato) +
// checkbox di selezione. Risolve la lamentela utente: «devo fare avanti
// per cercare il contatto, mi sembra macchinoso. Se ho tanti contatti
// divento pazzo».
function SelectContactsPhase({
  allContacts, selectedSet, setSelectedSet,
  searchQuery, setSearchQuery,
  filterGender, setFilterGender,
  filterStatus, setFilterStatus,
  matchInfo,
}) {
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allContacts
      .filter((c) => {
        if (filterGender && c.gender !== filterGender) return false;
        if (filterStatus && c.status !== filterStatus) return false;
        if (q) {
          const text = `${c.name || ""} ${c.phone || ""}`.toLowerCase();
          if (!text.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" }));
  }, [allContacts, searchQuery, filterGender, filterStatus]);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedSet(next);
  };

  const visibleIds = filtered.map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const toggleAllVisible = () => {
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      for (const id of visibleIds) next.delete(id);
    } else {
      for (const id of visibleIds) next.add(id);
    }
    setSelectedSet(next);
  };

  const statusBadge = (s) => {
    if (s === "caldo") return { label: "Da chiamare", color: "#5C6B4E" };
    if (s === "tiepido") return { label: "Da risentire", color: "#B45309" };
    if (s === "freddo") return { label: "In pausa", color: "#94A3B8" };
    return null;
  };

  return (
    <div className="space-y-3" data-testid="bulk-select-phase">
      {/* Toolbar ricerca + filtri */}
      <div className="space-y-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cerca per nome o telefono…"
          className="w-full px-3 py-2 text-[13px] bg-white border border-[#E8E5DE] rounded-lg focus:outline-none focus:border-[#7C8B6E]"
          data-testid="bulk-search-input"
        />
        <div className="flex gap-2 flex-wrap">
          {/* Filtro sesso */}
          <div className="flex items-center gap-0.5 bg-[#F5F4F0] rounded-md p-0.5">
            {[
              { v: "", l: "Tutti" },
              { v: "m", l: "Uomini" },
              { v: "f", l: "Donne" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setFilterGender(o.v)}
                className={`text-[11px] px-2.5 py-1 rounded ${filterGender === o.v ? "bg-white shadow-sm text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"}`}
                data-testid={`bulk-gender-${o.v || "all"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {/* Filtro stato */}
          <div className="flex items-center gap-0.5 bg-[#F5F4F0] rounded-md p-0.5">
            {[
              { v: "", l: "Tutti gli stati" },
              { v: "caldo", l: "Da chiamare" },
              { v: "tiepido", l: "Da risentire" },
              { v: "freddo", l: "In pausa" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setFilterStatus(o.v)}
                className={`text-[11px] px-2.5 py-1 rounded ${filterStatus === o.v ? "bg-white shadow-sm text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"}`}
                data-testid={`bulk-status-${o.v || "all"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Riga "seleziona tutti" */}
      <div className="flex items-center justify-between border-y border-[#EAE7DF] py-2 px-1">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAllVisible}
            className="w-4 h-4 accent-[#5C6B4E] cursor-pointer"
            data-testid="bulk-select-all"
          />
          <span className="text-[12.5px] text-[#1C1C1A] font-medium">
            {allVisibleSelected ? "Deseleziona tutti" : "Seleziona tutti"} ({filtered.length})
          </span>
        </label>
        <span className="text-[11px] text-[#9A968B]">
          {selectedSet.size}/{allContacts.length} totali
        </span>
      </div>

      {/* Lista contatti */}
      <div className="max-h-[300px] overflow-y-auto -mx-1 px-1 space-y-1">
        {filtered.length === 0 ? (
          <div className="py-8 text-center text-[12px] text-[#9A968B]">
            Nessun contatto trovato. Modifica ricerca o filtri.
          </div>
        ) : filtered.map((c) => {
          const isSelected = selectedSet.has(c.id);
          const badge = statusBadge(c.status);
          return (
            <label
              key={c.id}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                isSelected ? "bg-[#F1F4ED] border border-[#7C8B6E]/30" : "border border-transparent hover:bg-[#F8F7F2]"
              }`}
              data-testid={`bulk-contact-${c.id}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(c.id)}
                className="w-4 h-4 accent-[#5C6B4E] cursor-pointer shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#1C1C1A] truncate">{c.name || "(senza nome)"}</div>
                <div className="text-[11px] text-[#9A968B] truncate">{c.phone}</div>
                {/* iter v28 r401 — Quando il modal arriva da "Notifica clienti
                    interessati", mostra le ragioni del match (es. "cerca un
                    trilocale · interessato a Brera"). Aiuta a personalizzare
                    il messaggio prima di mandarlo. */}
                {matchInfo && matchInfo[c.id] && matchInfo[c.id].length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {matchInfo[c.id].map((r, i) => (
                      <span
                        key={i}
                        className="text-[10px] text-[#5C6B4E] bg-[#F1F4ED] px-1.5 py-0.5 rounded border border-[#7C8B6E]/20"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {badge && (
                <span
                  className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
                  style={{ background: `${badge.color}20`, color: badge.color }}
                >
                  {badge.label}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function PickPhase({ occasions, occasion, setOccasion, customText, setCustomText }) {
  const formatDays = (d) => {
    if (d === null || d === undefined) return null;
    if (d === 0) return "oggi";
    if (d === 1) return "domani";
    if (d <= 14) return `tra ${d} giorni`;
    if (d <= 60) return `tra ${Math.round(d / 7)} sett.`;
    return null;
  };

  const next = useMemo(() => occasions.filter((o) => o.days_until !== null && o.days_until <= 30), [occasions]);

  return (
    <div data-testid="bulk-greetings-pick">
      {next.length > 0 && (
        <>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#5A6A4D] font-semibold mb-2 inline-flex items-center gap-1.5">
            <Calendar size={12} weight="fill" /> In arrivo
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
            {next.map((o) => (
              <button
                key={o.key}
                onClick={() => setOccasion(o.key)}
                className={`text-left rounded-xl px-3 py-2.5 border transition-all ${
                  occasion === o.key
                    ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                    : "bg-white border-[#EAE7DF] hover:border-[#7C8B6E]"
                }`}
                data-testid={`bulk-greetings-occasion-${o.key}`}
              >
                <div className={`text-[13px] font-semibold leading-tight ${occasion === o.key ? "" : "text-[#1C1C1A]"}`}>
                  {o.label}
                </div>
                <div className={`text-[10.5px] mt-0.5 ${occasion === o.key ? "text-white/70" : "text-[#6B6A65]"}`}>
                  {formatDays(o.days_until)}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#5A6A4D] font-semibold mb-2">
        Tutte le occasioni
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
        {occasions.filter((o) => o.days_until === null || o.days_until > 30).map((o) => (
          <button
            key={o.key}
            onClick={() => setOccasion(o.key)}
            className={`text-left rounded-xl px-3 py-2.5 border transition-all ${
              occasion === o.key
                ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                : "bg-white border-[#EAE7DF] hover:border-[#7C8B6E]"
            }`}
            data-testid={`bulk-greetings-occasion-${o.key}`}
          >
            <div className={`text-[13px] font-semibold leading-tight ${occasion === o.key ? "" : "text-[#1C1C1A]"}`}>
              {o.label}
            </div>
            {o.days_until !== null && (
              <div className={`text-[10.5px] mt-0.5 ${occasion === o.key ? "text-white/70" : "text-[#6B6A65]"}`}>
                tra {o.days_until} giorni
              </div>
            )}
          </button>
        ))}
      </div>

      {occasion === "custom" && (
        <div className="mt-2">
          <label className="text-[11.5px] uppercase tracking-[0.16em] text-[#5A6A4D] font-semibold flex items-center gap-1.5 mb-2">
            <ChatCircle size={12} weight="fill" /> Cosa vuoi dire ai clienti?
          </label>
          <textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Es. comunico la chiusura per ferie dal 10 al 25 agosto…"
            rows={3}
            className="w-full text-[13px] px-3 py-2.5 border border-[#EAE7DF] rounded-lg focus:border-[#7C8B6E] focus:outline-none resize-none"
            data-testid="bulk-greetings-custom-text"
          />
          <p className="text-[11px] text-[#6B6A65] mt-1.5">
            Pronto lo riformula in italiano formale (Lei) per ogni contatto.
          </p>
        </div>
      )}
    </div>
  );
}

/* iter v29 r226 — Toggle "Personalizza con AI" — opt-in nella fase pick.
   Quando attivo, il backend usa LLM per arricchire ogni messaggio con il
   contesto del singolo cliente (note interne, ultima zona/budget cercato,
   ultimo immobile visto). Costa ~1 credito ogni 10 contatti. */
function PersonalizeToggle({ personalize, setPersonalize }) {
  return (
    <div className="mt-4 p-3 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-200" data-testid="bulk-greetings-personalize-toggle">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!personalize}
          onChange={(e) => setPersonalize(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#7C8B6E] cursor-pointer shrink-0"
          data-testid="bulk-greetings-personalize-checkbox"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkle size={13} weight="fill" className="text-amber-600" />
            <span className="text-[13px] font-semibold text-[#1C1C1A]">
              Personalizza per ogni cliente
            </span>
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
              1 cr / 10 contatti
            </span>
          </div>
          <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
            Pronto adatta il messaggio al singolo cliente usando le note, il budget
            e le preferenze in rubrica — sempre con il "Lei" formale. Esempio:{" "}
            <em>"Buongiorno Sig. Bianchi, …L'immobile rientra perfettamente nella
            zona e nel budget che mi aveva indicato."</em>
          </p>
        </div>
      </label>
    </div>
  );
}


/* iter v29 r226 — Phase SUMMARY — riepilogo statistiche di fine sessione.
   Mostra: contatti totali, inviati, saltati, durata. Gratifica + senso di
   completion. Pulsanti: chiudi · riprendi sequenza (se non tutti inviati). */
function SummaryPhase({ items, sentIds, occasion, startedAt, onRestart, onClose }) {
  const sent = items.filter((i) => sentIds[i.contact_id]).length;
  const skipped = items.length - sent;
  const durationMs = startedAt ? Math.max(0, Date.now() - startedAt) : 0;
  const durationMin = Math.round(durationMs / 60000);
  const durationLabel = durationMin < 1
    ? `${Math.round(durationMs / 1000)} secondi`
    : `${durationMin} ${durationMin === 1 ? "minuto" : "minuti"}`;
  const pct = items.length > 0 ? Math.round((sent / items.length) * 100) : 0;

  return (
    <div data-testid="bulk-greetings-summary" className="text-center py-4">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#7C8B6E]/15 mb-4">
        <ChartLineUp size={28} weight="duotone" className="text-[#5A6A4D]" />
      </div>
      <h3 className="font-editorial text-2xl text-[#1C1C1A] mb-1">
        {pct === 100 ? "Sessione completata" : "Ben fatto"}
      </h3>
      <p className="text-[13px] text-[#6B6A65] mb-6">
        {pct === 100
          ? "Hai mandato il messaggio a tutti i contatti scelti."
          : `Hai mandato a ${sent} su ${items.length} contatti.`}
      </p>

      <div className="grid grid-cols-3 gap-2 mb-6">
        <div className="bg-[#F8F7F2] rounded-xl p-3 border border-[#EAE7DF]">
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#7C8B6E] mb-1">Inviati</div>
          <div className="font-editorial text-2xl text-[#1C1C1A]" data-testid="summary-sent">{sent}</div>
        </div>
        <div className="bg-[#F8F7F2] rounded-xl p-3 border border-[#EAE7DF]">
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#9A968B] mb-1">Saltati</div>
          <div className="font-editorial text-2xl text-[#1C1C1A]" data-testid="summary-skipped">{skipped}</div>
        </div>
        <div className="bg-[#F8F7F2] rounded-xl p-3 border border-[#EAE7DF]">
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#9A968B] mb-1">Tempo</div>
          <div className="font-editorial text-base text-[#1C1C1A] leading-tight pt-1" data-testid="summary-time">{durationLabel}</div>
        </div>
      </div>

      {skipped > 0 ? (
        <div className="flex flex-col gap-2 items-center">
          <button
            onClick={onRestart}
            className="w-full max-w-xs inline-flex items-center justify-center gap-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-semibold py-2.5 rounded-lg"
            data-testid="summary-restart"
          >
            <ArrowRight size={13} weight="bold" />
            Continua sui {skipped} rimasti
          </button>
          <button
            onClick={onClose}
            className="text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium"
            data-testid="summary-close"
          >
            Chiudi · finisco dopo
          </button>
        </div>
      ) : (
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg"
          data-testid="summary-done"
        >
          <Check size={13} weight="bold" /> Chiudi
        </button>
      )}
    </div>
  );
}


function SendPhase({ item, sentIds, setSentIds, total, activeIdx, speed, setSpeed, personalized, onPrev, onNext, onClose, onFinish }) {
  const isSent = !!sentIds[item.contact_id];

  // iter v29 r043 — "Sequenza guidata" (Via 3 · semi-automatica) ROBUSTA.
  // iter v29 r226 — Velocità countdown ora CONFIGURABILE dall'utente:
  //   Veloce 2s · Normale 4s · Calmo 8s. Default 4s.
  const [autoMode, setAutoMode] = useState(false);
  const [autoPaused, setAutoPaused] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const STEP_S = speed || 4;

  const openWa = () => {
    setSentIds((s) => ({ ...s, [item.contact_id]: true }));
    window.open(item.wa_url, "_blank", "noopener");
  };

  // Countdown: parte SOLO quando l'utente ha già aperto WhatsApp di questo
  // contatto (sentIds[contact_id] === true). Così:
  // - Se non clicchi, Pronto non avanza mai (resta lì in attesa)
  // - Se clicchi WA + torni → countdown parte → auto-avanza
  useEffect(() => {
    if (!autoMode || autoPaused) return;
    if (!isSent) return; // aspetta il click su "Apri WhatsApp"

    setCountdown(STEP_S);
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(t);
          if (activeIdx < total - 1) {
            onNext();
          } else {
            setAutoMode(false);
          }
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMode, autoPaused, activeIdx, isSent]);

  // Page Visibility API: quando l'utente esce da Pronto (= apre WhatsApp),
  // mettiamo in pausa il countdown. Quando torna su Pronto, ripartiamo da
  // 4s freschi. Così il countdown non scorre mentre è su WA.
  useEffect(() => {
    if (!autoMode) return;
    const onVis = () => {
      if (document.hidden) {
        setAutoPaused(true);
      } else {
        setAutoPaused(false);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [autoMode]);

  const startAuto = () => {
    setAutoMode(true);
    setAutoPaused(false);
  };
  const pauseAuto = () => setAutoPaused(true);
  const resumeAuto = () => setAutoPaused(false);
  const stopAuto = () => {
    setAutoMode(false);
    setAutoPaused(false);
    setCountdown(0);
  };

  return (
    <div data-testid="bulk-greetings-send">
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/20">
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E]" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5A6A4D]">
            {activeIdx + 1} di {total}
          </span>
        </div>
      </div>

      {/* Auto-mode banner */}
      {autoMode && (
        <div
          className="mb-4 p-3 rounded-xl bg-[#1C1C1A] text-white flex items-center justify-between gap-3"
          data-testid="bulk-greetings-auto-banner"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative w-9 h-9 rounded-full bg-[#7C8B6E]/30 flex items-center justify-center shrink-0">
              {autoPaused ? (
                <span className="text-[11px] font-bold">II</span>
              ) : !isSent ? (
                <span className="text-[14px] font-bold">→</span>
              ) : (
                <span className="text-[14px] font-bold tabular-nums">{countdown}</span>
              )}
            </div>
            <div className="text-[11.5px] leading-snug min-w-0">
              <div className="font-semibold text-white">
                {autoPaused
                  ? "Sequenza in pausa"
                  : !isSent
                    ? "Apri WhatsApp per partire"
                    : `Prossimo contatto fra ${countdown}s`}
              </div>
              <div className="text-white/60 truncate">
                {autoPaused
                  ? "premi Riprendi per continuare"
                  : !isSent
                    ? "tap sul pulsante verde qui sotto"
                    : "manda su WhatsApp, poi torna qui"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* iter v29 r226 — Selettore velocità inline */}
            <div className="hidden sm:inline-flex items-center gap-0.5 bg-white/10 rounded-md p-0.5" data-testid="bulk-greetings-speed-selector">
              {[
                { v: 2, label: "Veloce" },
                { v: 4, label: "Normale" },
                { v: 8, label: "Calmo" },
              ].map((s) => (
                <button
                  key={s.v}
                  onClick={() => setSpeed(s.v)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                    speed === s.v ? "bg-white text-[#1C1C1A]" : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`bulk-greetings-speed-${s.v}`}
                  title={`Avanza dopo ${s.v} secondi`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {autoPaused ? (
              <button
                onClick={resumeAuto}
                className="px-3 py-1.5 rounded-md bg-[#7C8B6E] hover:bg-[#6B7A5D] text-[11.5px] font-semibold"
                data-testid="bulk-greetings-auto-resume"
              >
                Riprendi
              </button>
            ) : (
              <button
                onClick={pauseAuto}
                className="px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-[11.5px] font-semibold"
                data-testid="bulk-greetings-auto-pause"
              >
                Pausa
              </button>
            )}
            <button
              onClick={stopAuto}
              className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-[11.5px] font-medium"
              data-testid="bulk-greetings-auto-stop"
            >
              Stop
            </button>
          </div>
        </div>
      )}

      <div className="bg-[#F8F7F2] rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-[#7C8B6E]/15 text-[#5A6A4D] flex items-center justify-center font-semibold text-[14px]">
            {(item.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] font-semibold text-[#1C1C1A] truncate">{item.name}</div>
            <div className="text-[11.5px] text-[#6B6A65] flex items-center gap-1">
              <Phone size={11} weight="fill" /> {item.phone}
            </div>
          </div>
          {/* iter v29 r226 — Indicatore se è stato personalizzato dal LLM */}
          {personalized && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B5C3A] bg-amber-50 border border-amber-200 px-2 py-1 rounded-full"
              title="Messaggio adattato al contesto di questo cliente"
              data-testid="bulk-greetings-personalized-badge"
            >
              <Sparkle size={10} weight="fill" /> su misura
            </span>
          )}
          {isSent && (
            <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#5A6A4D] bg-[#7C8B6E]/15 px-2 py-1 rounded-full">
              <Check size={10} weight="bold" /> aperto
            </span>
          )}
        </div>

        <div className="bg-white border border-[#EAE7DF] rounded-xl p-3.5 text-[13px] text-[#1C1C1A] leading-relaxed whitespace-pre-wrap">
          {item.message}
        </div>
      </div>

      <button
        onClick={openWa}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white text-[14px] font-semibold py-3 rounded-xl mb-2"
        data-testid="bulk-greetings-open-wa"
      >
        <WhatsappLogo size={18} weight="fill" /> {isSent ? "Riapri WhatsApp" : "Apri WhatsApp"}
      </button>

      {/* CTA "Sequenza guidata" — un tap, Pronto apre WA uno per uno con
          un timer. L'utente preme "Invia" su ogni WhatsApp; Pronto avanza
          automaticamente. Tutta la legalità è preservata: il tap finale
          d'invio resta dell'utente. */}
      {!autoMode && activeIdx === 0 && total > 1 && (
        <button
          onClick={startAuto}
          className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-white text-[#1C1C1A] text-[13px] font-semibold py-2.5 rounded-xl mb-2 transition-colors"
          data-testid="bulk-greetings-auto-start"
        >
          <ArrowRight size={14} weight="bold" />
          Avvia sequenza guidata · apre WhatsApp uno per uno
        </button>
      )}

      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onPrev}
          disabled={activeIdx === 0 || autoMode}
          className="text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="bulk-greetings-prev"
        >
          ← Indietro
        </button>
        {activeIdx < total - 1 ? (
          <button
            onClick={onNext}
            disabled={autoMode}
            className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-4 py-2 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="bulk-greetings-next"
          >
            Avanti <ArrowRight size={13} weight="bold" />
          </button>
        ) : (
          <button
            onClick={onFinish}
            className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold px-4 py-2 rounded-lg"
            data-testid="bulk-greetings-finish"
          >
            <Check size={13} weight="bold" /> Fine · riepilogo
          </button>
        )}
      </div>

      {/* Teaser auto-invio WhatsApp Business — appare solo all'ultimo contatto */}
      {activeIdx === total - 1 && (
        <AutoSendTeaser />
      )}
    </div>
  );
}

/** Teaser discreto per l'add-on WhatsApp Business Auto-Send. Permette
 * all'agente di manifestare interesse. Fa una sola POST e non si ripresenta. */
function AutoSendTeaser() {
  const [status, setStatus] = useState("idle"); // idle | sending | done

  const interested = async () => {
    setStatus("sending");
    try {
      await api.post("/contacts/bulk-greetings/autosend-interest");
      setStatus("done");
      toast.success("Grazie! Ti scriviamo non appena è disponibile.");
    } catch {
      setStatus("idle");
      toast.error("Errore, riprova");
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-[#EAE7DF]" data-testid="autosend-teaser">
      <div className="flex items-start gap-3">
        <div className="text-[20px]" aria-hidden="true">⚡</div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-0.5">
            Stanco di aprire ogni chat?
          </div>
          <div className="text-[11.5px] text-[#6B6A65] leading-snug mb-2">
            Stiamo lavorando all'<strong className="text-[#1C1C1A]">auto-invio WhatsApp Business</strong> ufficiale Meta:
            manda un appartamento a 100 contatti con un click, dal tuo numero verificato.
            Add-on a <strong className="text-[#1C1C1A]">€29/mese</strong> · 500 messaggi inclusi.
          </div>
          {status === "done" ? (
            <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#5A6A4D]">
              <Check size={12} weight="bold" /> Sei in lista
            </div>
          ) : (
            <button
              type="button"
              onClick={interested}
              disabled={status === "sending"}
              className="text-[11.5px] font-semibold text-[#7C8B6E] hover:text-[#5A6A4D] disabled:opacity-50"
              data-testid="autosend-interest-btn"
            >
              {status === "sending" ? "Salvataggio…" : "Sì, mi interessa →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

